/**
 * Chapter026.js — Register of Injuries (OHSE 026)
 * Migrated from SWMS Platform Chapter026.tsx → JavaScript
 *
 * Props:
 *   data        {array}   injury_register from swmsData
 *   onChange    {fn}      (newEntries) => void
 *   companyName {string}  from companyInfo.name
 */

import { useCallback } from 'react';
import { Plus, Trash2 } from 'lucide-react';

const inputCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all';
const textareaCls = 'w-full px-2 py-1.5 text-sm bg-transparent border border-[#e0dbd0] rounded text-[#1a1a2e] placeholder:text-gray-400 focus:outline-none focus:border-[#c9a84c] focus:bg-white transition-all resize-y min-h-[60px]';

function emptyEntry() {
  return {
    id: crypto.randomUUID(),
    workplace_location: '', injured_persons_name: '', home_address: '',
    date_of_birth: '', gender: '', occupation: '', employers_name: '', employers_address: '',
    date_of_injury: '', time_of_injury: '',
    activity_at_time: '', exact_location: '', nature_of_injury: '', body_location: '',
    treatment_by_first_aid: '', treatment_remarks: '', follow_up_required: '',
    doctor_medical_centre: '', date_attended: '', medical_certificate_received: '',
    treatment_details: '', further_consultation: '', injury_management_required: '',
    name_of_witness: '', address_of_witness: '',
    first_aid_provider_name: '', first_aid_provider_signature: '',
  };
}

function YesNoSelect({ value, onChange }) {
  return (
    <select value={value || ''} onChange={e => onChange(e.target.value)}
      className="h-8 px-2 text-sm border border-[#e0dbd0] rounded bg-white text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c]">
      <option value="">—</option>
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  );
}

export default function Chapter026({ data = [], onChange, companyName }) {
  const cn = companyName || '[Company Name]';

  const addEntry = useCallback(() => onChange([...data, emptyEntry()]), [data, onChange]);

  const removeEntry = useCallback((id) => onChange(data.filter(e => e.id !== id)), [data, onChange]);

  const updateEntry = useCallback((id, field, value) => {
    onChange(data.map(e => e.id === id ? { ...e, [field]: value } : e));
  }, [data, onChange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-[#c9a84c] uppercase tracking-widest mb-1">OHSE 026</p>
        <h2 className="text-xl font-bold text-[#1a1a2e] uppercase tracking-wide">Register of Injuries</h2>
      </div>

      {/* Declaration */}
      <div className="p-4 bg-[#f8f7f4] rounded-lg border border-[#e0dbd0] text-sm text-[#1a1a2e] leading-relaxed">
        <span className="font-semibold text-[#c9a84c]">{cn}</span>{' '}
        records all injuries in the following register.
      </div>

      {/* Records */}
      {data.map((entry) => (
        <InjuryCard key={entry.id} entry={entry}
          onUpdate={(field, value) => updateEntry(entry.id, field, value)}
          onRemove={() => removeEntry(entry.id)} />
      ))}

      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center border border-[#e0dbd0] rounded-lg bg-white">
          <p className="text-sm text-gray-400 mb-3">No injury records yet.</p>
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-[#1a1a2e] rounded hover:bg-[#252547] transition-colors">
            <Plus className="w-4 h-4" /> Add Injury Record
          </button>
        </div>
      )}

      {data.length > 0 && (
        <div className="flex justify-center">
          <button type="button" onClick={addEntry}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1a1a2e] rounded hover:bg-[rgba(201,168,76,0.15)] border border-[#c9a84c]/40 transition-colors">
            <Plus className="w-4 h-4" /> Add Injury Record
          </button>
        </div>
      )}
    </div>
  );
}

function InjuryCard({ entry, onUpdate, onRemove }) {
  const u = (field) => (e) => onUpdate(field, e.target.value);

  return (
    <div className="bg-white border border-[#e0dbd0] rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 bg-[#f0ede6] flex items-center justify-between">
        <span className="text-sm font-semibold text-[#1a1a2e] uppercase tracking-wide">
          {entry.injured_persons_name || 'Injury Record'}
        </span>
        <button type="button" onClick={onRemove} className="p-1 text-red-400 hover:text-red-600 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-5">
        {/* General */}
        <Section title="General">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Workplace Location"><input type="text" value={entry.workplace_location || ''} placeholder="Location" onChange={u('workplace_location')} className={inputCls} /></Field>
            <Field label="Injured Person's Name"><input type="text" value={entry.injured_persons_name || ''} placeholder="Full name" onChange={u('injured_persons_name')} className={inputCls} /></Field>
            <Field label="Home Address"><input type="text" value={entry.home_address || ''} placeholder="Home address" onChange={u('home_address')} className={inputCls} /></Field>
            <Field label="Date of Birth"><input type="date" value={entry.date_of_birth || ''} onChange={u('date_of_birth')} className={inputCls} /></Field>
            <Field label="Gender">
              <select value={entry.gender || ''} onChange={u('gender')}
                className="w-full h-9 px-2 text-sm border border-[#e0dbd0] rounded bg-white text-[#1a1a2e] focus:outline-none focus:border-[#c9a84c]">
                <option value="">—</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </Field>
            <Field label="Occupation"><input type="text" value={entry.occupation || ''} placeholder="Occupation" onChange={u('occupation')} className={inputCls} /></Field>
            <Field label="Employer's Name"><input type="text" value={entry.employers_name || ''} placeholder="Employer name" onChange={u('employers_name')} className={inputCls} /></Field>
            <Field label="Employer's Address"><input type="text" value={entry.employers_address || ''} placeholder="Employer address" onChange={u('employers_address')} className={inputCls} /></Field>
          </div>
        </Section>

        {/* Details of Injury */}
        <Section title="Details of Injury">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Date of Injury"><input type="date" value={entry.date_of_injury || ''} onChange={u('date_of_injury')} className={inputCls} /></Field>
            <Field label="Time of Injury (24HR)"><input type="text" value={entry.time_of_injury || ''} placeholder="e.g. 14:30" onChange={u('time_of_injury')} className={inputCls} /></Field>
            <Field label="Activity at Time of Injury"><input type="text" value={entry.activity_at_time || ''} placeholder="What were they doing?" onChange={u('activity_at_time')} className={inputCls} /></Field>
            <Field label="Exact Location of Injury"><input type="text" value={entry.exact_location || ''} placeholder="Where did it happen?" onChange={u('exact_location')} className={inputCls} /></Field>
            <Field label="Nature of Injury"><input type="text" value={entry.nature_of_injury || ''} placeholder="e.g. Cut, Strain, Fracture" onChange={u('nature_of_injury')} className={inputCls} /></Field>
            <Field label="Body Location of Injury"><input type="text" value={entry.body_location || ''} placeholder="e.g. Left hand, Lower back" onChange={u('body_location')} className={inputCls} /></Field>
          </div>
        </Section>

        {/* Details of Treatment */}
        <Section title="Details of Treatment">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Treatment by First Aid Officer">
              <YesNoSelect value={entry.treatment_by_first_aid} onChange={v => onUpdate('treatment_by_first_aid', v)} />
            </Field>
            <Field label="Remarks"><input type="text" value={entry.treatment_remarks || ''} placeholder="Treatment remarks" onChange={u('treatment_remarks')} className={inputCls} /></Field>
            <Field label="Follow-up Treatment Required">
              <YesNoSelect value={entry.follow_up_required} onChange={v => onUpdate('follow_up_required', v)} />
            </Field>
            <Field label="Doctor / Medical Centre"><input type="text" value={entry.doctor_medical_centre || ''} placeholder="Name of doctor/clinic" onChange={u('doctor_medical_centre')} className={inputCls} /></Field>
            <Field label="Date Attended"><input type="date" value={entry.date_attended || ''} onChange={u('date_attended')} className={inputCls} /></Field>
            <Field label="Medical Certificate Received">
              <YesNoSelect value={entry.medical_certificate_received} onChange={v => onUpdate('medical_certificate_received', v)} />
            </Field>
            <Field label="Further Consultation Required">
              <YesNoSelect value={entry.further_consultation} onChange={v => onUpdate('further_consultation', v)} />
            </Field>
            <Field label="Injury Management Required">
              <YesNoSelect value={entry.injury_management_required} onChange={v => onUpdate('injury_management_required', v)} />
            </Field>
            <Field label="Treatment Details (e.g. x-ray, prescription)" className="md:col-span-2">
              <textarea value={entry.treatment_details || ''} placeholder="Details of treatment received..." onChange={u('treatment_details')} className={textareaCls} />
            </Field>
          </div>
        </Section>

        {/* Witness & First Aid Provider */}
        <Section title="Witness &amp; First Aid Provider">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Name of Witness"><input type="text" value={entry.name_of_witness || ''} placeholder="Witness name" onChange={u('name_of_witness')} className={inputCls} /></Field>
            <Field label="Address of Witness"><input type="text" value={entry.address_of_witness || ''} placeholder="Witness address" onChange={u('address_of_witness')} className={inputCls} /></Field>
            <Field label="Name of Person Providing First Aid"><input type="text" value={entry.first_aid_provider_name || ''} placeholder="First aid provider name" onChange={u('first_aid_provider_name')} className={inputCls} /></Field>
            <Field label="Signature (First Aid Provider)"><input type="text" value={entry.first_aid_provider_signature || ''} placeholder="Signature (type name)" onChange={u('first_aid_provider_signature')} className={inputCls} /></Field>
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 pb-1 border-b border-[#e0dbd0]">
        {title}
      </h4>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
